import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQDonutChart, { DonutChartParts } from '../../components/donut-chart';
import UQSpacer from '../../components/spacer';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import UQCode from '../../components/code';
import { BREADCRUMB_ROOT } from '../../common/config';

const donutParts: DonutChartParts[] = [
  { name: 'Completed', percentage: 40, value: '40' },
  { name: 'In Progress', percentage: 30, value: '30' },
  { name: 'Overdue', percentage: 20, value: '20' },
  { name: 'Warning', percentage: 10, value: '10' },
];

const attributes = [
  { attribute: 'parts', type: 'DonutChartParts[]', default: '[]', description: 'Array of chart segments.' },
  { attribute: 'showTotal', type: 'boolean', default: 'false', description: 'Show the total value in the center.' },
  { attribute: 'getLabel', type: '(name: string) => string', default: 'undefined', description: 'Custom label renderer for legend.' },
  { attribute: 'thickness', type: "'s' | 'm' | 'l'", default: "'m'", description: 'Thickness of the donut ring: small (s), medium (m), or large (l).' },
];

const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
  { columnId: 'attribute', renderHeaderCell: () => 'Attribute', renderCell: (item) => item.attribute },
  { columnId: 'type', renderHeaderCell: () => 'Type', renderCell: (item) => <code>{item.type}</code> },
  { columnId: 'default', renderHeaderCell: () => 'Default', renderCell: (item) => <code>{item.default}</code> },
  { columnId: 'description', renderHeaderCell: () => 'Description', renderCell: (item) => item.description },
];

const codeExample = `
import UQDonutChart from '@uniqconsulting/ui/components/donut-chart';

const parts = [
  { name: 'Completed', percentage: 40, value: '40' },
  { name: 'In Progress', percentage: 30, value: '30' },
  { name: 'Overdue', percentage: 20, value: '20' },
  { name: 'Warning', percentage: 10, value: '10' },
];

<UQDonutChart parts={parts} showTotal thickness="m" getLabel={name => name} />
`;

const ChartsDemoPage = () => (
  <>
    <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Charts']} />
    <h1>Charts Demo</h1>
    <UQSpacer size="m" />

    {/* Description */}
    <h2>Description</h2>
    <p>
      The <code>UQDonutChart</code> component visualizes data as a donut chart with a legend and optional total. You can control the thickness of the donut ring using the <code>thickness</code> prop (<code>'s'</code>, <code>'m'</code>, or <code>'l'</code>). More chart types (Pie, Linear, etc.) will be added soon.
    </p>
    <UQSpacer size="m" />

    {/* Attributes Table */}
    <h2>Attributes</h2>
    <UQSpacer size="s" />
    <UQDataGrid
      items={attributes}
      columns={attributeColumns}
      sortable={false}
      selectionMode="none"
      rowLines
    />
    <UQSpacer size="m" />

    {/* Live Demo */}
    <h2>Donut Chart Example</h2>
    <UQSpacer size="s" />
    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <div>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>Small</div>
        <UQDonutChart parts={donutParts} showTotal thickness="s" getLabel={name => name} />
      </div>
      <div>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>Medium (default)</div>
        <UQDonutChart parts={donutParts} showTotal thickness="m" getLabel={name => name} />
      </div>
      <div>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>Large</div>
        <UQDonutChart parts={donutParts} showTotal thickness="l" getLabel={name => name} />
      </div>
    </div>
    <UQSpacer size="m" />

    {/* Code Example */}
    <h2>Code Example</h2>
    <UQCode code={codeExample.trim()} enableCopy />
  </>
);

export default ChartsDemoPage;